/**
 * Suite Dashboard
 *
 * Main wrapper component for the ContentSage AI Suite mobile experience.
 * Orchestrates navigation between:
 * - Home (Stats & Quick Actions)
 * - Apps Grid (Tool Selection)
 * - Individual App Screens (BlogCraft, PlanCraft, Calendar)
 * - History (Generated Content)
 * - Services (Agency Marketing)
 *
 * This is the v2/v4 experience for iOS/Android/Web (not Wix).
 */

import React, { useState, useCallback, useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { MobileHeader } from './MobileHeader';
import { SuiteBottomNav, type SuiteTabId } from './SuiteBottomNav';
import { MobileHomePage } from './MobileHomePage';
import { AppsGridPage, type AppId } from './AppsGridPage';
import { MobileCreatePage } from './MobileCreatePage';
import { MobileHistoryPage } from './MobileHistoryPage';
import { MobileServicesPage } from './MobileServicesPage';
import { PlanCraftPage } from './PlanCraftPage';
import { CalendarPage } from './CalendarPage';
import { PricingPage } from './PricingPage';
import { BrandProfilesPage } from './BrandProfilesPage';
import { AnalyticsDashboardPage } from './AnalyticsDashboardPage';
import { PaymentSuccessModal } from './PaymentSuccessModal';
import MFAEnrollment from '../auth/MFAEnrollment';
import { CreditPurchaseModal } from './CreditPurchaseModal';
import { useAuth } from '../../contexts/AuthContext';

const API_URL = import.meta.env.VITE_API_URL || 'https://claude-essay-agent-production.up.railway.app';

// Extended tab type that includes app-specific screens
type ActiveScreen =
  | SuiteTabId
  | 'blogcraft'
  | 'plancraft'
  | 'calendar'
  | 'seobooster'
  | 'pricing'
  | 'brand-profiles';

interface SuiteDashboardProps {
  getAuthHeaders?: () => Promise<Record<string, string>>;
}

export const SuiteDashboard: React.FC<SuiteDashboardProps> = ({
  getAuthHeaders,
}) => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<SuiteTabId>('home');
  const [activeScreen, setActiveScreen] = useState<ActiveScreen>('home');
  const [showSettings, setShowSettings] = useState(false);
  const [showMFASetup, setShowMFASetup] = useState(false);
  const [showCreditPurchase, setShowCreditPurchase] = useState(false);
  const [showPaymentSuccess, setShowPaymentSuccess] = useState(false);

  // Fetch user credits from API
  const { getAuthToken } = useAuth();

  // Stats fetched from analytics overview API
  const [stats, setStats] = useState({
    postsGenerated: 0,
    wordsWritten: 0,
    knowledgeDocs: 0,
  });

  // Fetch real stats from analytics overview
  useEffect(() => {
    const fetchStats = async () => {
      try {
        const token = await getAuthToken();
        if (!token) return;
        const res = await fetch(`${API_URL}/api/v1/analytics/overview?period=30d`, {
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        });
        if (res.ok) {
          const data = await res.json();
          setStats({
            postsGenerated: data.content_generated || 0,
            wordsWritten: (data.content_generated || 0) * 800, // estimate ~800 words per post
            knowledgeDocs: 0, // will be fetched from knowledge vault API if available
          });
        }
      } catch {
        // Silently fail — stats cards will show 0
      }
    };
    if (user) fetchStats();
  }, [user, getAuthToken]);

  // Recent posts fetched from API
  const [recentPosts, setRecentPosts] = useState<Array<{
    id: string;
    title: string;
    date: string;
    status: 'draft' | 'published' | 'processing';
  }>>([]);

  useEffect(() => {
    const fetchRecentPosts = async () => {
      try {
        const token = await getAuthToken();
        if (!token) return;
        const res = await fetch(`${API_URL}/api/v1/blog/history?limit=3`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          setRecentPosts(data.map((item: { id: string; title: string; created_at: string; status: string }) => ({
            id: item.id,
            title: item.title || 'Untitled',
            date: new Date(item.created_at).toLocaleDateString('en', { month: 'short', day: 'numeric' }),
            status: item.status === 'completed' ? 'published' as const : item.status === 'processing' ? 'processing' as const : 'draft' as const,
          })));
        }
      } catch {
        // Silently fail
      }
    };
    if (user) fetchRecentPosts();
  }, [user, getAuthToken]);

  // User plan/credits - fetched from API
  const [userPlan, setUserPlan] = useState<'free' | 'starter' | 'professional' | 'agency'>('free');
  const [credits, setCredits] = useState(0);
  const [_creditsLoading, setCreditsLoading] = useState(true);

  const fetchCredits = useCallback(async () => {
    try {
      const token = await getAuthToken();
      if (!token) {
        setCreditsLoading(false);
        return;
      }

      const response = await fetch(`${API_URL}/api/v1/payments/credits`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setCredits(data.credits || 0);
        // Map plan types
        const planMap: Record<string, 'free' | 'starter' | 'professional' | 'agency'> = {
          'free': 'free',
          'starter': 'starter',
          'pro': 'professional',
          'agency': 'agency',
        };
        setUserPlan(planMap[data.plan] || 'free');
      }
    } catch (error) {
      console.error('Error fetching credits:', error);
    } finally {
      setCreditsLoading(false);
    }
  }, [getAuthToken]);

  // Fetch credits on mount and when user changes
  useEffect(() => {
    if (user) {
      fetchCredits();
    }
  }, [user, fetchCredits]);

  // Check for payment success URL param
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const paymentStatus = urlParams.get('payment');

    if (paymentStatus === 'success') {
      // Clean up URL immediately
      const newUrl = window.location.pathname;
      window.history.replaceState({}, '', newUrl);

      // Poll for updated plan data (webhook may take a moment to process)
      let attempts = 0;
      const maxAttempts = 5;
      const pollInterval = 1500; // 1.5 seconds

      const pollForUpdate = async () => {
        attempts++;
        await fetchCredits();

        // If we have credits and plan is not free, or max attempts reached, show modal
        if (attempts >= maxAttempts) {
          setShowPaymentSuccess(true);
        } else {
          // Check again after interval
          setTimeout(pollForUpdate, pollInterval);
        }
      };

      // Start polling after a short delay to let webhook process
      setTimeout(pollForUpdate, 1000);
    }
  }, [fetchCredits]);

  // Refresh credits after purchase
  const handleCreditsPurchased = useCallback(() => {
    fetchCredits();
  }, [fetchCredits]);

  // Navigation handlers
  const handleTabChange = useCallback((tab: SuiteTabId) => {
    setActiveTab(tab);
    setActiveScreen(tab);
  }, []);

  const handleAppSelect = useCallback((appId: AppId) => {
    setActiveScreen(appId);
  }, []);

  const handleBackToApps = useCallback(() => {
    setActiveTab('apps');
    setActiveScreen('apps');
  }, []);

  const handleNavigateFromHome = useCallback((tab: SuiteTabId | 'create') => {
    if (tab === 'create') {
      // Navigate to BlogCraft (the blog creation tool)
      setActiveTab('apps');
      setActiveScreen('blogcraft');
    } else {
      setActiveTab(tab as SuiteTabId);
      setActiveScreen(tab as SuiteTabId);
    }
  }, []);

  const handleGenerateContent = useCallback((entryId: string) => {
    // Navigate to BlogCraft with the calendar entry
    console.log('Generate content for entry:', entryId);
    setActiveScreen('blogcraft');
  }, []);

  // Check if we're in an app-specific screen (not a main tab)
  const isInAppScreen = ['blogcraft', 'plancraft', 'calendar', 'seobooster', 'pricing'].includes(
    activeScreen
  );

  // Navigation to pricing page
  const handleNavigateToPricing = useCallback(() => {
    setActiveScreen('pricing');
  }, []);

  const renderContent = () => {
    const pageVariants = {
      initial: { opacity: 0, x: 20 },
      animate: { opacity: 1, x: 0 },
      exit: { opacity: 0, x: -20 },
    };

    switch (activeScreen) {
      // Main Tab Screens
      case 'home':
        return (
          <motion.div
            key="home"
            variants={pageVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            transition={{ duration: 0.2 }}
          >
            <MobileHomePage
              onNavigate={handleNavigateFromHome}
              recentPosts={recentPosts}
              stats={stats}
            />
          </motion.div>
        );

      case 'apps':
        return (
          <motion.div
            key="apps"
            variants={pageVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            transition={{ duration: 0.2 }}
          >
            <AppsGridPage
              onAppSelect={handleAppSelect}
              userPlan={userPlan}
              credits={credits}
              onNavigateToPricing={handleNavigateToPricing}
            />
          </motion.div>
        );

      case 'history':
        return (
          <motion.div
            key="history"
            variants={pageVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            transition={{ duration: 0.2 }}
          >
            <MobileHistoryPage getAuthHeaders={getAuthHeaders} />
          </motion.div>
        );

      case 'analytics':
        return (
          <motion.div
            key="analytics"
            variants={pageVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            transition={{ duration: 0.2 }}
            className="h-full"
          >
            <AnalyticsDashboardPage
              getAuthHeaders={getAuthHeaders}
            />
          </motion.div>
        );

      case 'services':
        return (
          <motion.div
            key="services"
            variants={pageVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            transition={{ duration: 0.2 }}
          >
            <MobileServicesPage />
          </motion.div>
        );

      // App-Specific Screens
      case 'blogcraft':
        return (
          <motion.div
            key="blogcraft"
            variants={pageVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            transition={{ duration: 0.2 }}
            className="h-full"
          >
            <MobileCreatePage
              getAuthHeaders={getAuthHeaders}
              onComplete={() => handleTabChange('history')}
              onBack={handleBackToApps}
              showBackButton={true}
              userPlan={userPlan}
              credits={credits}
              onNavigateToPricing={handleNavigateToPricing}
              onSettingsClick={() => setShowSettings(true)}
              onMFAClick={() => setShowMFASetup(true)}
            />
          </motion.div>
        );

      case 'plancraft':
        return (
          <motion.div
            key="plancraft"
            variants={pageVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            transition={{ duration: 0.2 }}
            className="h-full"
          >
            <PlanCraftPage
              onBack={handleBackToApps}
              getAuthHeaders={getAuthHeaders}
              onSettingsClick={() => setShowSettings(true)}
              onMFAClick={() => setShowMFASetup(true)}
            />
          </motion.div>
        );

      case 'calendar':
        return (
          <motion.div
            key="calendar"
            variants={pageVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            transition={{ duration: 0.2 }}
            className="h-full"
          >
            <CalendarPage
              onBack={handleBackToApps}
              onGenerateContent={handleGenerateContent}
              getAuthHeaders={getAuthHeaders}
              onSettingsClick={() => setShowSettings(true)}
              onMFAClick={() => setShowMFASetup(true)}
            />
          </motion.div>
        );

      case 'pricing':
        return (
          <motion.div
            key="pricing"
            variants={pageVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            transition={{ duration: 0.2 }}
            className="h-full"
          >
            <PricingPage
              onBack={handleBackToApps}
              currentCredits={credits}
              onPurchaseComplete={handleCreditsPurchased}
            />
          </motion.div>
        );

      case 'brand-profiles':
        return (
          <motion.div
            key="brand-profiles"
            variants={pageVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            transition={{ duration: 0.2 }}
            className="h-full"
          >
            <BrandProfilesPage
              onBack={handleBackToApps}
              getAuthHeaders={getAuthHeaders}
              apiBase={API_URL}
            />
          </motion.div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header - Hidden when in app-specific screens */}
      {!isInAppScreen && (
        <MobileHeader
          onSettingsClick={() => setShowSettings(true)}
          onMFAClick={() => setShowMFASetup(true)}
        />
      )}

      {/* Main Content */}
      <main className={`flex-1 ${isInAppScreen ? '' : 'overflow-y-auto'}`}>
        <AnimatePresence mode="wait">{renderContent()}</AnimatePresence>
      </main>

      {/* Bottom Navigation - Hidden when in app-specific screens */}
      {!isInAppScreen && (
        <SuiteBottomNav activeTab={activeTab} onTabChange={handleTabChange} />
      )}

      {/* MFA Setup Modal */}
      <AnimatePresence>
        {showMFASetup && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-end justify-center z-50"
            onClick={(e) => {
              if (e.target === e.currentTarget) setShowMFASetup(false);
            }}
          >
            <motion.div
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="w-full max-h-[90vh] bg-white rounded-t-3xl overflow-y-auto pb-[env(safe-area-inset-bottom)]"
            >
              <div className="sticky top-0 bg-white px-4 py-3 border-b border-gray-100 flex items-center justify-center">
                <div className="w-12 h-1 bg-gray-300 rounded-full" />
              </div>
              <div className="p-4">
                <MFAEnrollment
                  onComplete={() => setShowMFASetup(false)}
                  onSkip={() => setShowMFASetup(false)}
                />
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Settings Bottom Sheet */}
      <AnimatePresence>
        {showSettings && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-end justify-center z-50"
            onClick={(e) => {
              if (e.target === e.currentTarget) setShowSettings(false);
            }}
          >
            <motion.div
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="w-full bg-white rounded-t-3xl overflow-hidden pb-[env(safe-area-inset-bottom)]"
            >
              <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-center">
                <div className="w-12 h-1 bg-gray-300 rounded-full" />
              </div>
              <div className="p-4 space-y-4">
                <h2 className="text-xl font-bold text-gray-900">Settings</h2>

                {/* Account Section */}
                <div className="bg-gray-50 rounded-xl p-4">
                  <h3 className="text-sm font-medium text-gray-500 mb-3">Account</h3>
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center text-indigo-600 font-bold text-lg">
                      {user?.displayName?.charAt(0) || 'U'}
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{user?.displayName}</p>
                      <p className="text-sm text-gray-500">{user?.email}</p>
                    </div>
                  </div>
                </div>

                {/* Subscription Section */}
                <div className="bg-gray-50 rounded-xl p-4">
                  <h3 className="text-sm font-medium text-gray-500 mb-3">Subscription</h3>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-gray-900 capitalize">{userPlan} Plan</p>
                      <p className="text-sm text-gray-500">{credits} credits remaining</p>
                    </div>
                    <button
                      onClick={() => {
                        setShowSettings(false);
                        handleNavigateToPricing();
                      }}
                      className="px-3 py-1.5 bg-indigo-100 text-indigo-700 rounded-lg text-sm font-medium"
                    >
                      Upgrade
                    </button>
                  </div>
                </div>

                {/* Security Section */}
                <div className="bg-gray-50 rounded-xl p-4">
                  <h3 className="text-sm font-medium text-gray-500 mb-3">Security</h3>
                  <button
                    onClick={() => {
                      setShowSettings(false);
                      setShowMFASetup(true);
                    }}
                    className="w-full flex items-center justify-between p-3 bg-white rounded-lg"
                  >
                    <span className="font-medium text-gray-900">Two-Factor Auth</span>
                    <span
                      className={`text-sm px-2 py-1 rounded-full ${
                        user?.mfaEnabled
                          ? 'bg-green-100 text-green-700'
                          : 'bg-amber-100 text-amber-700'
                      }`}
                    >
                      {user?.mfaEnabled ? 'Enabled' : 'Not Set'}
                    </span>
                  </button>
                </div>

                {/* Platform Connections */}
                <div className="bg-gray-50 rounded-xl p-4">
                  <h3 className="text-sm font-medium text-gray-500 mb-3">Publishing</h3>
                  <button
                    onClick={() => {
                      window.location.href = '/settings';
                    }}
                    className="w-full flex items-center justify-between p-3 bg-white rounded-lg"
                  >
                    <span className="font-medium text-gray-900">Platform Connections</span>
                    <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </div>

                {/* Close Button */}
                <button
                  onClick={() => setShowSettings(false)}
                  className="w-full py-3 px-4 bg-gray-200 text-gray-700 rounded-xl font-medium touch-target"
                >
                  Close
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Credit Purchase Modal */}
      <CreditPurchaseModal
        isOpen={showCreditPurchase}
        onClose={() => setShowCreditPurchase(false)}
        currentCredits={credits}
        requiredCredits={1}
        serviceName="Credits"
        onPurchaseComplete={handleCreditsPurchased}
      />

      {/* Payment Success Modal */}
      <PaymentSuccessModal
        isOpen={showPaymentSuccess}
        onClose={() => setShowPaymentSuccess(false)}
        planName={userPlan === 'professional' ? 'Pro' : userPlan.charAt(0).toUpperCase() + userPlan.slice(1)}
        credits={credits}
      />
    </div>
  );
};

export default SuiteDashboard;
